import { useCallback, useRef } from "react";
import { IjmSelection } from "../utils";
import { useNavigation } from "@react-navigation/native";
import { StackScreenProps } from "@react-navigation/stack";
import { RootStackParamList } from "../navigations/RootNavigation";
import type { NavigationState } from "@react-navigation/routers";
import { Cct, EarningType } from "../__generated__/graphql";
import { useSetAtom } from "jotai";
import { editIjmSelectionCallback } from "../atoms/core";

type ScreenParams = StackScreenProps<RootStackParamList>;

type CallBackType = (selection: IjmSelection | null) => void | Promise<void>;

type CallBackRefType = {
    callback: CallBackType;
    state: NavigationState<RootStackParamList>;
};

export type EditIjmSalaryInfo = {
    amount: number;
    earningType: EarningType;
};

const extractLastRouteName = (
    state: NavigationState<RootStackParamList>,
): Extract<keyof RootStackParamList, string> | undefined => {
    const routes = state.routes;
    if (routes.length === 0) {
        return undefined;
    }
    const lastRoute = routes[routes.length - 1];
    return lastRoute.name;
};
/**
 * This will bring the IJM editor on screen and set the selection ( or new editor / empty selection)
 * usage:
 * const currentSelection = // assume this exists and is of type IjmSelection or {} for empty editor
 * const ijmEditor = useIJMEditor();
 * ijmEditor({currentSelection, salaryInfo: {amound: 1000, earningType: "package"}}, async (selection) => {
 *       // handle selection null=> cancel, not null => have been saved
 *   });
 */
export const useIJMEditor = () => {
    const callbackRef = useRef<CallBackRefType>(undefined);
    const navigation = useNavigation<ScreenParams["navigation"]>();
    const setOnSave = useSetAtom(editIjmSelectionCallback);
    return useCallback(
        (
            options: {
                currentSelection?: IjmSelection | null;
                salaryInfo?: EditIjmSalaryInfo | null;
                cct?: Cct | null;
                useLse?: boolean;
            },
            onChange: CallBackType,
        ) => {
            const state = navigation.getState();
            callbackRef.current = { callback: onChange, state };

            const onSave = async (selection: IjmSelection | null) => {
                const callback = callbackRef.current?.callback;
                const savedState = callbackRef.current?.state;

                if (selection) {
                    await callback?.(selection);
                } else {
                    await callback?.(null);
                }

                callbackRef.current = undefined;

                if (savedState) {
                    const lastRouteName = extractLastRouteName(savedState);
                    if (lastRouteName) {
                        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                        // @ts-expect-error
                        navigation.navigate(lastRouteName);
                        return;
                    }
                }
                navigation.pop();
            };
            setOnSave({ func: onSave });
            navigation.navigate("IJMEditorNavScreen", {
                ijmSelection: {
                    ijmGroupId: options.currentSelection?.ijmGroupId,
                    ijmGroupName: options.currentSelection?.ijmGroupName,
                    ijmPayFirstDays: options.currentSelection?.ijmPayFirstDays,
                    ijmPayRemainingPercents: options.currentSelection?.ijmPayRemainingPercents,
                },
                cct: options.cct ?? undefined,
                useLse: options.useLse ?? true,
                salaryInfo: options.salaryInfo || undefined,
            });
        },
        [navigation, setOnSave],
    );
};
